import { useState, useEffect } from "react";

// ── Sidebar Menu Items ────────────────────────────────────────────────────────
const MENU = [
  { id: "dashboard",   label: "Dashboard",        icon: "⊞" },
  { id: "users",       label: "User Management",  icon: "👥" },
  { id: "attendance",  label: "Attendance",       icon: "📅" },
  { id: "tasks",       label: "Task Management",  icon: "✅" },
  { id: "leaves",      label: "Leave Management", icon: "🗓" },
  { id: "documents",   label: "Documents",        icon: "📄" },
  { id: "settings",    label: "Settings",         icon: "⚙" },
];

// ── Colour tokens ─────────────────────────────────────────────────────────────
const C = {
  sidebar:  "linear-gradient(180deg,#312e81 0%,#3730a3 40%,#4338ca 100%)",
  accent:   "#6366f1",
  accentDk: "#4f46e5",
  bg:       "#f1f5f9",
  card:     "#ffffff",
  border:   "#e2e8f0",
  text:     "#1e293b",
  sub:      "#64748b",
  muted:    "#94a3b8",
};

// ── Tiny Chart helpers ────────────────────────────────────────────────────────
const BarChart = ({ data, colors }) => {
  const max = Math.max(...data.flatMap(d => d.values));
  return (
    <svg width="100%" height="110" viewBox="0 0 320 110" preserveAspectRatio="none">
      {data.map((d, gi) =>
        d.values.map((v, i) => {
          const barW = 18, gap = 6, groupW = d.values.length * (barW + gap);
          const totalGroups = data[0].values.length;
          const slotW = 320 / totalGroups;
          const x = i * slotW + gi * (barW + gap) + (slotW - groupW) / 2 + 10;
          const h = (v / max) * 85;
          return <rect key={`${gi}-${i}`} x={x} y={110 - h - 15} width={barW} height={h} rx={3} fill={colors[gi]} />;
        })
      )}
      {["Jan","Feb","Mar","Apr","May","Jun"].map((m,i)=>(
        <text key={m} x={i*(320/6)+26} y={108} fontSize={9} fill={C.muted} textAnchor="middle">{m}</text>
      ))}
    </svg>
  );
};

const AreaChart = () => {
  const fulfilled = [380,320,260,300,320,360];
  const pending   = [580,560,520,580,600,590];
  const W=320,H=110,pad=15;
  const pts = (arr) => arr.map((v,i)=>`${pad+i*((W-2*pad)/5)},${H-pad-(v/700)*(H-2*pad)}`).join(" ");
  const area = (arr) => `M${pad},${H-pad} ${arr.map((v,i)=>`L${pad+i*((W-2*pad)/5)},${H-pad-(v/700)*(H-2*pad)}`).join(" ")} L${W-pad},${H-pad} Z`;
  return (
    <svg width="100%" height="110" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none">
      <defs>
        <linearGradient id="gF" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#10b981" stopOpacity=".7"/><stop offset="100%" stopColor="#10b981" stopOpacity=".1"/></linearGradient>
        <linearGradient id="gP" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#a78bfa" stopOpacity=".7"/><stop offset="100%" stopColor="#a78bfa" stopOpacity=".1"/></linearGradient>
      </defs>
      <path d={area(pending)}   fill="url(#gP)" />
      <path d={area(fulfilled)} fill="url(#gF)" />
      <polyline points={pts(pending)}   fill="none" stroke="#a78bfa" strokeWidth="2"/>
      <polyline points={pts(fulfilled)} fill="none" stroke="#10b981" strokeWidth="2"/>
      {["Jan","Feb","Mar","Apr","May","Jun"].map((m,i)=>(
        <text key={m} x={pad+i*((W-2*pad)/5)} y={H-2} fontSize={9} fill={C.muted} textAnchor="middle">{m}</text>
      ))}
    </svg>
  );
};

// ── Screen Components ─────────────────────────────────────────────────────────

const Dashboard = () => (
  <div style={{padding:16,overflowY:"auto",height:"100%"}}>
    <div style={{marginBottom:16}}>
      <h2 style={{fontSize:20,fontWeight:700,color:C.text,margin:0}}>Dashboard</h2>
      <p style={{fontSize:12,color:C.sub,margin:"4px 0 0"}}>Welcome back! Here's what's happening today.</p>
    </div>

    {/* Stat Cards */}
    <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,marginBottom:16}}>
      {[
        {label:"TOTAL USERS",       val:"8",        change:"+12%", icon:"👥", iconBg:"#ede9fe", iconC:"#7c3aed"},
        {label:"ACTIVE INQUIRIES",  val:"1",        change:"+8%",  icon:"💬", iconBg:"#d1fae5", iconC:"#059669"},
        {label:"TOTAL SERVICES",    val:"8",        change:"0%",   icon:"💼", iconBg:"#ede9fe", iconC:"#7c3aed"},
        {label:"MONTHLY REVENUE",   val:"₹45,600",  change:"+18%", icon:"💲", iconBg:"#fef3c7", iconC:"#d97706"},
      ].map(({label,val,change,icon,iconBg,iconC})=>(
        <div key={label} style={{background:C.card,borderRadius:12,padding:"14px 16px",border:`1px solid ${C.border}`,boxShadow:"0 1px 4px rgba(0,0,0,0.06)"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:10}}>
            <div style={{width:38,height:38,borderRadius:10,background:iconBg,display:"flex",alignItems:"center",justifyContent:"center",fontSize:16}}>{icon}</div>
            <span style={{fontSize:10,fontWeight:600,color:change==="0%"?"#94a3b8":change.startsWith("+")?"#059669":"#dc2626",background:change==="0%"?"#f1f5f9":change.startsWith("+")?"#d1fae5":"#fee2e2",padding:"2px 7px",borderRadius:20}}>{change}</span>
          </div>
          <div style={{fontSize:10,color:C.muted,fontWeight:600,letterSpacing:"0.05em",marginBottom:4}}>{label}</div>
          <div style={{fontSize:22,fontWeight:800,color:C.text}}>{val}</div>
          <div style={{fontSize:10,color:C.muted,marginTop:2}}>from last month</div>
        </div>
      ))}
    </div>

    {/* Charts Row */}
    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:16}}>
      <div style={{background:C.card,borderRadius:12,padding:14,border:`1px solid ${C.border}`}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
          <span style={{fontSize:12,fontWeight:700,color:C.text}}>Performance Level</span>
          <div style={{display:"flex",gap:10,fontSize:10,color:C.sub}}>
            <span>🟡 Volume</span><span>⬛ Service</span>
          </div>
        </div>
        <BarChart data={[{values:[65,75,68,55,80,90]},{values:[42,50,58,62,48,68]}]} colors={["#f59e0b","#94a3b8"]}/>
      </div>
      <div style={{background:C.card,borderRadius:12,padding:14,border:`1px solid ${C.border}`}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
          <span style={{fontSize:12,fontWeight:700,color:C.text}}>Customer Fulfillment</span>
          <div style={{display:"flex",gap:10,fontSize:10,color:C.sub}}>
            <span>🟢 Fulfilled</span><span>🟣 Pending</span>
          </div>
        </div>
        <AreaChart/>
      </div>
    </div>

    {/* Bottom Row */}
    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
      <div style={{background:C.card,borderRadius:12,padding:14,border:`1px solid ${C.border}`}}>
        <div style={{display:"flex",justifyContent:"space-between",marginBottom:10}}>
          <span style={{fontSize:12,fontWeight:700,color:C.text}}>Top Products</span>
          <span style={{fontSize:11,color:C.accent,cursor:"pointer",fontWeight:600}}>View All</span>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"30px 1fr 80px 60px",gap:6,fontSize:10,color:C.muted,fontWeight:600,marginBottom:8,borderBottom:`1px solid ${C.border}`,paddingBottom:6}}>
          <span>#</span><span>NAME</span><span>POPULARITY</span><span>SALES</span>
        </div>
        {[["1","EMS Portal","████████","₹18,200"],["2","CRM Suite","██████","₹14,800"],["3","HR Module","████","₹12,600"]].map(([n,name,pop,sales])=>(
          <div key={n} style={{display:"grid",gridTemplateColumns:"30px 1fr 80px 60px",gap:6,fontSize:11,padding:"5px 0",borderBottom:`1px solid ${C.border}`,alignItems:"center"}}>
            <span style={{color:C.muted}}>{n}</span>
            <span style={{fontWeight:600,color:C.text}}>{name}</span>
            <span style={{color:C.accent,fontSize:10}}>{pop}</span>
            <span style={{fontWeight:600,color:"#059669"}}>{sales}</span>
          </div>
        ))}
      </div>
      <div style={{background:C.card,borderRadius:12,padding:14,border:`1px solid ${C.border}`}}>
        <div style={{display:"flex",justifyContent:"space-between",marginBottom:10}}>
          <span style={{fontSize:12,fontWeight:700,color:C.text}}>Recent Activity</span>
          <span style={{fontSize:11,color:C.accent,cursor:"pointer",fontWeight:600}}>View All</span>
        </div>
        {[
          {dot:"#6366f1",text:"New user registered: gbdbd",time:"2 days ago"},
          {dot:"#10b981",text:"Omkar Chauhan marked Present",time:"3 days ago"},
          {dot:"#f59e0b",text:"Leave approved: Sick Leave",time:"5 days ago"},
          {dot:"#ef4444",text:"Document uploaded: Aadhar",time:"1 week ago"},
        ].map(({dot,text,time})=>(
          <div key={text} style={{display:"flex",gap:10,alignItems:"flex-start",padding:"7px 0",borderBottom:`1px solid ${C.border}`}}>
            <div style={{width:8,height:8,borderRadius:"50%",background:dot,marginTop:3,flexShrink:0}}/>
            <div>
              <div style={{fontSize:11,color:C.text,fontWeight:500}}>{text}</div>
              <div style={{fontSize:10,color:C.muted}}>{time}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const Users = () => (
  <div style={{padding:16,overflowY:"auto",height:"100%"}}>
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:16}}>
      <div>
        <h2 style={{fontSize:20,fontWeight:700,color:C.text,margin:0}}>User Management</h2>
        <p style={{fontSize:12,color:C.sub,margin:"4px 0 0"}}>Manage user accounts and permissions</p>
      </div>
      <button style={{display:"flex",alignItems:"center",gap:6,padding:"8px 16px",background:C.accentDk,color:"#fff",border:"none",borderRadius:9,fontSize:12,fontWeight:600,cursor:"pointer"}}>
        ＋ Add New User
      </button>
    </div>
    <div style={{background:C.card,borderRadius:12,border:`1px solid ${C.border}`,overflow:"hidden"}}>
      <div style={{padding:"12px 14px",borderBottom:`1px solid ${C.border}`,display:"flex",gap:10}}>
        <div style={{flex:1,display:"flex",alignItems:"center",gap:8,background:C.bg,borderRadius:8,padding:"7px 12px"}}>
          <span style={{color:C.muted}}>🔍</span>
          <span style={{fontSize:12,color:C.muted}}>Search users...</span>
        </div>
        <select style={{padding:"7px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:12,color:C.sub,background:"#fff"}}>
          <option>All Roles</option><option>User</option><option>Employee</option><option>Admin</option>
        </select>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"2fr 2.5fr 1fr 1fr 1.2fr 1.2fr",fontSize:10,fontWeight:700,color:C.muted,padding:"10px 14px",borderBottom:`1px solid ${C.border}`,letterSpacing:"0.05em"}}>
        <span>NAME</span><span>EMAIL</span><span>ROLE</span><span>STATUS</span><span>JOIN DATE</span><span>ACTIONS</span>
      </div>
      {[
        {init:"G",  name:"gbdbd",           email:"bhavesbfbffsbhsattavan@gmail.com", role:"User",     initBg:"#6366f1", date:"Mar 2, 2026"},
        {init:"BS", name:"Bhavesh Sattavan", email:"bhaveshsattavan@gmail.com",        role:"User",     initBg:"#6366f1", date:"Feb 17, 2026"},
        {init:"OC", name:"Omkar Chauhan",    email:"omkarchauhan2004@gmail.com",        role:"User",     initBg:"#6366f1", date:"Feb 11, 2026"},
        {init:"NJ", name:"Nikhil Jaiswal",   email:"jaiswalnikhil712@gmail.com",        role:"Employee", initBg:"#6366f1", date:"Feb 11, 2026"},
        {init:"MS", name:"Manish Sharma",    email:"akashchandeltg154679@gmail.com",    role:"Employee", initBg:"#6366f1", date:"Feb 11, 2026"},
        {init:"AN", name:"Ayush Nigam",      email:"ayushnigam2004@gmail.com",          role:"Employee", initBg:"#6366f1", date:"Feb 11, 2026"},
        {init:"MM", name:"Mayank Mishra",    email:"mayankmishr81@gmail.com",           role:"Employee", initBg:"#6366f1", date:"Feb 11, 2026"},
      ].map(({init,name,email,role,initBg,date})=>(
        <div key={name} style={{display:"grid",gridTemplateColumns:"2fr 2.5fr 1fr 1fr 1.2fr 1.2fr",padding:"10px 14px",borderBottom:`1px solid ${C.border}`,alignItems:"center",fontSize:12}}>
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            <div style={{width:30,height:30,borderRadius:"50%",background:initBg,display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",fontSize:11,fontWeight:700,flexShrink:0}}>{init}</div>
            <span style={{fontWeight:600,color:C.text}}>{name}</span>
          </div>
          <span style={{color:C.sub,fontSize:11,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{email}</span>
          <span style={{color:C.text}}>{role}</span>
          <span style={{display:"inline-flex"}}><span style={{padding:"2px 8px",borderRadius:20,background:"#d1fae5",color:"#065f46",fontSize:10,fontWeight:600}}>Active</span></span>
          <span style={{color:C.sub,fontSize:11}}>{date}</span>
          <div style={{display:"flex",gap:5}}>
            {[["👁","#dbeafe","#1e40af"],["✏","#d1fae5","#065f46"],["🗑","#fee2e2","#991b1b"]].map(([ic,bg,cl])=>(
              <button key={ic} style={{width:26,height:26,borderRadius:7,border:"none",background:bg,color:cl,cursor:"pointer",fontSize:12,display:"flex",alignItems:"center",justifyContent:"center"}}>{ic}</button>
            ))}
          </div>
        </div>
      ))}
    </div>
  </div>
);

const Attendance = () => (
  <div style={{padding:16,overflowY:"auto",height:"100%"}}>
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:16}}>
      <div>
        <h2 style={{fontSize:20,fontWeight:700,color:C.text,margin:0}}>Attendance Management</h2>
        <p style={{fontSize:12,color:C.sub,margin:"4px 0 0"}}>Mark and track employee attendance</p>
      </div>
      <div style={{display:"flex",gap:8}}>
        <button style={{padding:"7px 14px",background:"#fff",border:`1.5px solid ${C.border}`,borderRadius:9,fontSize:12,fontWeight:600,color:C.text,cursor:"pointer"}}>📅 Mark Single</button>
        <button style={{padding:"7px 14px",background:C.accentDk,color:"#fff",border:"none",borderRadius:9,fontSize:12,fontWeight:600,cursor:"pointer"}}>📅 Bulk Mark</button>
      </div>
    </div>

    {/* Search */}
    <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:8,padding:"8px 12px",marginBottom:12,display:"flex",alignItems:"center",gap:8}}>
      <span style={{color:C.muted}}>🔍</span>
      <span style={{fontSize:12,color:C.muted}}>Search employees...</span>
    </div>

    {/* Today Stats */}
    <div style={{fontSize:12,fontWeight:600,color:C.sub,marginBottom:8}}>📋 Today's Stats — Thursday, 5 March</div>
    <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:10,marginBottom:14}}>
      {[
        {val:"7",label:"Present Today",   bg:"#fff7ed",dot:"#f97316"},
        {val:"0",label:"Absent Today",    bg:"#fff1f2",dot:"#ef4444"},
        {val:"1",label:"Half Day Today",  bg:"#fefce8",dot:"#eab308"},
        {val:"0",label:"On Leave Today",  bg:"#eff6ff",dot:"#3b82f6"},
      ].map(({val,label,bg,dot})=>(
        <div key={label} style={{background:bg,borderRadius:12,padding:"12px 14px",border:`1px solid ${C.border}`}}>
          <div style={{width:12,height:12,borderRadius:"50%",background:dot,marginBottom:8}}/>
          <div style={{fontSize:24,fontWeight:800,color:C.text,marginBottom:4}}>{val}</div>
          <div style={{fontSize:11,color:C.sub}}>{label}</div>
        </div>
      ))}
    </div>

    {/* Table */}
    <div style={{background:C.card,borderRadius:12,border:`1px solid ${C.border}`,overflow:"hidden"}}>
      <div style={{display:"grid",gridTemplateColumns:"2fr 1.2fr 1.2fr 1fr 1fr 2fr 1fr",fontSize:10,fontWeight:700,color:C.muted,padding:"10px 14px",borderBottom:`1px solid ${C.border}`,letterSpacing:"0.05em"}}>
        <span>EMPLOYEE</span><span>DATE</span><span>STATUS</span><span>CHECK IN</span><span>CHECK OUT</span><span>REMARKS</span><span>ACTIONS</span>
      </div>
      {[
        {init:"O",name:"Omkar Chauhan", emp:"EMP0005",date:"2026-03-05",status:"Present",  si:"#d1fae5",sc:"#065f46",ci:"09:15 AM",co:"06:30 PM",rem:"On time"},
        {init:"N",name:"Nikhil Jaiswal",emp:"EMP0010",date:"2026-03-05",status:"Present",  si:"#d1fae5",sc:"#065f46",ci:"09:20 AM",co:"06:25 PM",rem:"On time"},
        {init:"M",name:"Manish Sharma", emp:"EMP0009",date:"2026-03-05",status:"Present",  si:"#d1fae5",sc:"#065f46",ci:"09:30 AM",co:"06:10 PM",rem:"Slight delay"},
        {init:"A",name:"Ayush Nigam",   emp:"EMP0008",date:"2026-03-04",status:"Logged In",si:"#fef3c7",sc:"#92400e",ci:"09:35 AM",co:"—",       rem:"Checkout pending"},
        {init:"B",name:"Bhavesh Sattan",emp:"EMP0007",date:"2026-02-27",status:"Present",  si:"#d1fae5",sc:"#065f46",ci:"09:45 AM",co:"06:00 PM",rem:"On time"},
        {init:"O",name:"Omkar Chauhan", emp:"EMP0005",date:"2026-02-26",status:"Absent",   si:"#fee2e2",sc:"#991b1b",ci:"—",       co:"—",       rem:"Late login >11 AM"},
      ].map(({init,name,emp,date,status,si,sc,ci,co,rem})=>(
        <div key={`${name}${date}`} style={{display:"grid",gridTemplateColumns:"2fr 1.2fr 1.2fr 1fr 1fr 2fr 1fr",padding:"9px 14px",borderBottom:`1px solid ${C.border}`,alignItems:"center",fontSize:11}}>
          <div style={{display:"flex",alignItems:"center",gap:7}}>
            <div style={{width:28,height:28,borderRadius:"50%",background:"#6366f1",display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",fontSize:11,fontWeight:700,flexShrink:0}}>{init}</div>
            <div><div style={{fontWeight:600,color:C.text,fontSize:11}}>{name}</div><div style={{fontSize:9,color:C.muted}}>{emp}</div></div>
          </div>
          <span style={{color:C.sub,fontSize:10}}>{date}</span>
          <span><span style={{padding:"2px 7px",borderRadius:20,background:si,color:sc,fontSize:10,fontWeight:600}}>{status}</span></span>
          <span style={{color:C.text}}>{ci}</span>
          <span style={{color:C.text}}>{co}</span>
          <span style={{color:C.sub,fontSize:10}}>{rem}</span>
          <button style={{padding:"3px 10px",background:"#fee2e2",color:"#dc2626",border:"none",borderRadius:6,fontSize:10,fontWeight:600,cursor:"pointer"}}>Delete</button>
        </div>
      ))}
    </div>
  </div>
);

const Leaves = () => (
  <div style={{padding:16,overflowY:"auto",height:"100%"}}>
    <div style={{marginBottom:16}}>
      <h2 style={{fontSize:20,fontWeight:700,color:C.text,margin:0}}>Leave Management</h2>
      <p style={{fontSize:12,color:C.sub,margin:"4px 0 0"}}>Review and manage employee leave requests</p>
    </div>

    {/* Stats */}
    <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:10,marginBottom:14}}>
      {[
        {val:"4",label:"Total Requests",color:C.accentDk},
        {val:"1",label:"Pending",        color:"#f59e0b"},
        {val:"2",label:"Approved",       color:"#059669"},
        {val:"1",label:"Rejected",       color:"#ef4444"},
      ].map(({val,label,color})=>(
        <div key={label} style={{background:C.card,borderRadius:12,padding:"14px 16px",border:`1px solid ${C.border}`}}>
          <div style={{fontSize:28,fontWeight:800,color,marginBottom:4}}>{val}</div>
          <div style={{fontSize:12,color:C.sub}}>{label}</div>
        </div>
      ))}
    </div>

    {/* Filters */}
    <div style={{display:"flex",gap:8,marginBottom:12}}>
      <div style={{flex:1,background:C.card,border:`1px solid ${C.border}`,borderRadius:8,padding:"8px 12px",display:"flex",alignItems:"center",gap:8}}>
        <span style={{color:C.muted}}>🔍</span><span style={{fontSize:12,color:C.muted}}>Search by employee name or ID...</span>
      </div>
      <select style={{padding:"7px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:12,color:C.sub,background:"#fff"}}>
        <option>All Status</option><option>Pending</option><option>Approved</option><option>Rejected</option>
      </select>
      <select style={{padding:"7px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:12,color:C.sub,background:"#fff"}}>
        <option>All Types</option><option>Sick Leave</option><option>Earned Leave</option><option>Casual Leave</option>
      </select>
    </div>

    {/* Table */}
    <div style={{background:C.card,borderRadius:12,border:`1px solid ${C.border}`,overflow:"hidden"}}>
      <div style={{display:"grid",gridTemplateColumns:"2fr 1.2fr 1fr 1fr 0.5fr 1fr 1fr 1.2fr 1fr",fontSize:10,fontWeight:700,color:C.muted,padding:"10px 14px",borderBottom:`1px solid ${C.border}`,letterSpacing:"0.05em"}}>
        <span>EMPLOYEE</span><span>LEAVE TYPE</span><span>START</span><span>END</span><span>DAYS</span><span>REASON</span><span>APPLIED ON</span><span>STATUS</span><span>ACTIONS</span>
      </div>
      {[
        {init:"O",name:"Omkar Chauhan",emp:"EMP0005",type:"Sick Leave",  typeC:"#fee2e2",typeTx:"#dc2626",start:"2/26/2026",end:"2/27/2026",days:"2",reason:"Fever",applied:"2/26/2026",status:"Approved",sb:"#d1fae5",sc:"#065f46"},
        {init:"O",name:"Omkar Chauhan",emp:"EMP0005",type:"Earned Leave",typeC:"#dbeafe",typeTx:"#1d4ed8",start:"2/27/2026",end:"2/28/2026",days:"2",reason:"Personal",applied:"2/23/2026",status:"Approved",sb:"#d1fae5",sc:"#065f46"},
        {init:"N",name:"Nikhil Jaiswal",emp:"EMP0010",type:"Casual Leave",typeC:"#f3e8ff",typeTx:"#7c3aed",start:"3/10/2026",end:"3/12/2026",days:"3",reason:"Trip",applied:"3/01/2026",status:"Pending",sb:"#fef3c7",sc:"#92400e"},
        {init:"M",name:"Manish Sharma", emp:"EMP0009",type:"Sick Leave",  typeC:"#fee2e2",typeTx:"#dc2626",start:"3/05/2026",end:"3/05/2026",days:"1",reason:"Unwell",applied:"3/04/2026",status:"Rejected",sb:"#fee2e2",sc:"#991b1b"},
      ].map(({init,name,emp,type,typeC,typeTx,start,end,days,reason,applied,status,sb,sc})=>(
        <div key={`${name}${start}`} style={{display:"grid",gridTemplateColumns:"2fr 1.2fr 1fr 1fr 0.5fr 1fr 1fr 1.2fr 1fr",padding:"9px 14px",borderBottom:`1px solid ${C.border}`,alignItems:"center",fontSize:11}}>
          <div style={{display:"flex",alignItems:"center",gap:7}}>
            <div style={{width:28,height:28,borderRadius:"50%",background:"#6366f1",display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",fontSize:11,fontWeight:700,flexShrink:0}}>{init}</div>
            <div><div style={{fontWeight:600,color:C.text}}>{name}</div><div style={{fontSize:9,color:C.muted}}>{emp}</div></div>
          </div>
          <span><span style={{padding:"2px 7px",borderRadius:20,background:typeC,color:typeTx,fontSize:10,fontWeight:600}}>{type}</span></span>
          <span style={{color:C.sub}}>{start}</span>
          <span style={{color:C.sub}}>{end}</span>
          <span style={{fontWeight:600,color:C.text}}>{days}</span>
          <span style={{color:C.sub}}>{reason}</span>
          <span style={{color:C.sub}}>{applied}</span>
          <span><span style={{padding:"2px 7px",borderRadius:20,background:sb,color:sc,fontSize:10,fontWeight:600,display:"flex",alignItems:"center",gap:4}}><span>{status==="Approved"?"✅":status==="Pending"?"⏳":"❌"}</span>{status}</span></span>
          <span style={{color:C.muted,fontSize:12}}>—</span>
        </div>
      ))}
    </div>
  </div>
);

const Documents = () => (
  <div style={{padding:16,overflowY:"auto",height:"100%"}}>
    {/* Header — same light style as other screens */}
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:16}}>
      <div>
        <h2 style={{fontSize:20,fontWeight:700,color:C.text,margin:0}}>Document Management</h2>
        <p style={{fontSize:12,color:C.sub,margin:"4px 0 0"}}>View and manage all employee uploaded documents</p>
      </div>
    </div>
    {/* Stat Cards — light like other screens */}
    <div style={{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:10,marginBottom:14}}>
      {[
        {label:"TOTAL",      val:"2", color:C.accentDk, bg:"#ede9fe"},
        {label:"ONBOARDING", val:"0", color:"#d97706",  bg:"#fef3c7"},
        {label:"IDENTITY",   val:"1", color:"#7c3aed",  bg:"#f3e8ff"},
        {label:"TAX",        val:"0", color:"#059669",  bg:"#d1fae5"},
        {label:"BENEFITS",   val:"0", color:"#dc2626",  bg:"#fee2e2"},
      ].map(({label,val,color,bg})=>(
        <div key={label} style={{background:C.card,borderRadius:12,padding:"12px 14px",border:`1px solid ${C.border}`,boxShadow:"0 1px 4px rgba(0,0,0,0.05)"}}>
          <div style={{display:"flex",alignItems:"center",gap:5,marginBottom:8}}>
            <div style={{width:7,height:7,borderRadius:"50%",background:color}}/>
            <span style={{fontSize:9,color:C.muted,fontWeight:700,letterSpacing:"0.06em"}}>{label}</span>
          </div>
          <div style={{fontSize:24,fontWeight:800,color}}>{val}</div>
        </div>
      ))}
    </div>

    {/* Search & Filter */}
    <div style={{display:"flex",gap:8,marginBottom:12}}>
      <div style={{flex:1,background:C.card,border:`1px solid ${C.border}`,borderRadius:8,padding:"8px 12px",display:"flex",alignItems:"center",gap:8}}>
        <span style={{color:C.muted}}>🔍</span><span style={{fontSize:12,color:C.muted}}>Search by document name or employee...</span>
      </div>
      <select style={{padding:"7px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:12,color:C.sub,background:"#fff"}}>
        <option>All Categories</option><option>Identity</option><option>Tax</option><option>Onboarding</option>
      </select>
    </div>

    {/* Table */}
    <div style={{background:C.card,borderRadius:12,border:`1px solid ${C.border}`,overflow:"hidden"}}>
      <div style={{display:"grid",gridTemplateColumns:"2.5fr 1.5fr 1fr 0.8fr 1fr 1.5fr",fontSize:10,fontWeight:700,color:C.muted,padding:"10px 14px",borderBottom:`1px solid ${C.border}`,letterSpacing:"0.05em"}}>
        <span>DOCUMENT</span><span>EMPLOYEE</span><span>CATEGORY</span><span>SIZE</span><span>UPLOADED</span><span>ACTIONS</span>
      </div>
      {[
        {docIcon:"🟥",name:"Python_Certificate",type:"PDF",empInit:"O",empName:"Omkar Chauhan",empId:"EMP0005",cat:"Other",   catC:"#f1f5f9",catTx:"#475569",size:"1.1 MB",date:"27/2/2026"},
        {docIcon:"🖼",name:"Screenshot 2025-11-06",type:"PNG",empInit:"O",empName:"Omkar Chauhan",empId:"EMP0005",cat:"Identity",catC:"#ede9fe",catTx:"#6d28d9",size:"148 KB",date:"26/2/2026"},
      ].map(({docIcon,name,type,empInit,empName,empId,cat,catC,catTx,size,date})=>(
        <div key={name} style={{display:"grid",gridTemplateColumns:"2.5fr 1.5fr 1fr 0.8fr 1fr 1.5fr",padding:"11px 14px",borderBottom:`1px solid ${C.border}`,alignItems:"center",fontSize:11}}>
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            <span style={{fontSize:18}}>{docIcon}</span>
            <div><div style={{fontWeight:600,color:C.text}}>{name}</div><div style={{fontSize:10,color:C.muted}}>{type}</div></div>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:7}}>
            <div style={{width:26,height:26,borderRadius:"50%",background:"#6366f1",display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",fontSize:10,fontWeight:700}}>{empInit}</div>
            <div><div style={{fontWeight:500,color:C.text,fontSize:11}}>{empName}</div><div style={{fontSize:9,color:C.muted}}>{empId}</div></div>
          </div>
          <span><span style={{padding:"2px 8px",borderRadius:20,background:catC,color:catTx,fontSize:10,fontWeight:600}}>{cat}</span></span>
          <span style={{color:C.sub}}>{size}</span>
          <span style={{color:C.sub}}>{date}</span>
          <div style={{display:"flex",gap:6}}>
            <button style={{padding:"4px 10px",background:"#2563eb",color:"#fff",border:"none",borderRadius:7,fontSize:10,fontWeight:600,cursor:"pointer"}}>👁 View</button>
            <button style={{padding:"4px 10px",background:"#059669",color:"#fff",border:"none",borderRadius:7,fontSize:10,fontWeight:600,cursor:"pointer"}}>⬇ Download</button>
            <button style={{padding:"4px 8px",background:"#fee2e2",border:"none",borderRadius:7,fontSize:12,cursor:"pointer"}}>🗑</button>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const Settings = () => (
  <div style={{padding:16,overflowY:"auto",height:"100%"}}>
    <div style={{marginBottom:16}}>
      <h2 style={{fontSize:20,fontWeight:700,color:C.text,margin:0}}>Settings</h2>
      <p style={{fontSize:12,color:C.sub,margin:"4px 0 0"}}>Manage your application preferences</p>
    </div>
    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
      {[
        {title:"Company Info",  icon:"🏢", fields:[["Company Name","OneNest Connect"],["Email","admin@onenest.in"],["Phone","+91 9876543210"],["Website","onenest.in"]]},
        {title:"Admin Account", icon:"👤", fields:[["Full Name","Admin User"],["Email","admin@onenest.in"],["Role","Super Admin"],["Last Login","Mar 05, 2026"]]},
        {title:"Notifications", icon:"🔔", fields:[["New User Alert","Enabled"],["Leave Requests","Enabled"],["Attendance Alert","Disabled"],["Monthly Report","Enabled"]]},
        {title:"Security",      icon:"🔒", fields:[["2FA","Enabled"],["Session Timeout","30 min"],["Password Expiry","90 days"],["Login Attempts","5 max"]]},
      ].map(({title,icon,fields})=>(
        <div key={title} style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:12,padding:14}}>
          <div style={{fontSize:13,fontWeight:700,color:C.text,marginBottom:12,display:"flex",alignItems:"center",gap:6}}>{icon} {title}</div>
          {fields.map(([l,v])=>(
            <div key={l} style={{marginBottom:10}}>
              <div style={{fontSize:10,color:C.muted,marginBottom:3}}>{l}</div>
              <div style={{padding:"6px 10px",background:C.bg,borderRadius:7,fontSize:12,color:C.text,fontWeight:500,border:`1px solid ${C.border}`}}>{v}</div>
            </div>
          ))}
        </div>
      ))}
    </div>
  </div>
);


const Tasks = () => (
  <div style={{padding:16,overflowY:"auto",height:"100%"}}>
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:16}}>
      <div>
        <h2 style={{fontSize:20,fontWeight:700,color:C.text,margin:0}}>Task Management</h2>
        <p style={{fontSize:12,color:C.sub,margin:"4px 0 0"}}>Manage and track all employee tasks</p>
      </div>
      <button style={{display:"flex",alignItems:"center",gap:6,padding:"8px 16px",background:C.accentDk,color:"#fff",border:"none",borderRadius:9,fontSize:12,fontWeight:600,cursor:"pointer"}}>
        ＋ Add New Task
      </button>
    </div>

    {/* Stat Cards */}
    <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:10,marginBottom:14}}>
      {[
        {val:"12",label:"Total Tasks",    color:C.accentDk,  bg:"#ede9fe"},
        {val:"4", label:"Pending",        color:"#d97706",   bg:"#fef3c7"},
        {val:"5", label:"In Progress",    color:"#2563eb",   bg:"#dbeafe"},
        {val:"3", label:"Completed",      color:"#059669",   bg:"#d1fae5"},
      ].map(({val,label,color,bg})=>(
        <div key={label} style={{background:C.card,borderRadius:12,padding:"14px 16px",border:`1px solid ${C.border}`,boxShadow:"0 1px 4px rgba(0,0,0,0.06)"}}>
          <div style={{width:36,height:36,borderRadius:10,background:bg,display:"flex",alignItems:"center",justifyContent:"center",marginBottom:10}}>
            <span style={{fontSize:16}}>{label==="Total Tasks"?"📋":label==="Pending"?"⏳":label==="In Progress"?"🔄":"✅"}</span>
          </div>
          <div style={{fontSize:26,fontWeight:800,color,marginBottom:2}}>{val}</div>
          <div style={{fontSize:11,color:C.sub}}>{label}</div>
        </div>
      ))}
    </div>

    {/* Filters */}
    <div style={{display:"flex",gap:8,marginBottom:12}}>
      <div style={{flex:1,background:C.card,border:`1px solid ${C.border}`,borderRadius:8,padding:"8px 12px",display:"flex",alignItems:"center",gap:8}}>
        <span style={{color:C.muted}}>🔍</span>
        <span style={{fontSize:12,color:C.muted}}>Search tasks...</span>
      </div>
      <select style={{padding:"7px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:12,color:C.sub,background:"#fff"}}>
        <option>All Status</option><option>Pending</option><option>In Progress</option><option>Completed</option>
      </select>
      <select style={{padding:"7px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:12,color:C.sub,background:"#fff"}}>
        <option>All Priority</option><option>Urgent</option><option>High</option><option>Medium</option><option>Low</option>
      </select>
    </div>

    {/* Task Table */}
    <div style={{background:C.card,borderRadius:12,border:`1px solid ${C.border}`,overflow:"hidden"}}>
      <div style={{display:"grid",gridTemplateColumns:"2.5fr 1.5fr 1fr 1fr 1.2fr 1.2fr 1.2fr",fontSize:10,fontWeight:700,color:C.muted,padding:"10px 14px",borderBottom:`1px solid ${C.border}`,letterSpacing:"0.05em"}}>
        <span>TASK</span><span>ASSIGNED TO</span><span>PRIORITY</span><span>STATUS</span><span>DUE DATE</span><span>CREATED</span><span>ACTIONS</span>
      </div>
      {[
        {title:"Complete Q1 Report",       assignee:"Omkar Chauhan",  init:"OC", priority:"Urgent",  ps:"#fee2e2",pc:"#dc2626", status:"In Progress", ss:"#dbeafe",sc:"#1d4ed8", due:"Mar 10, 2026",created:"Mar 01"},
        {title:"Update Employee Records",  assignee:"Nikhil Jaiswal", init:"NJ", priority:"High",    ps:"#fff7ed",pc:"#c2410c", status:"Pending",     ss:"#fef9c3",sc:"#854d0e", due:"Mar 12, 2026",created:"Mar 02"},
        {title:"Review Policy Documents",  assignee:"Manish Sharma",  init:"MS", priority:"Medium",  ps:"#f0fdf4",pc:"#15803d", status:"Completed",   ss:"#dcfce7",sc:"#166534", due:"Mar 01, 2026",created:"Feb 25"},
        {title:"Prepare Monthly Payslips", assignee:"Ayush Nigam",    init:"AN", priority:"Urgent",  ps:"#fee2e2",pc:"#dc2626", status:"Pending",     ss:"#fef9c3",sc:"#854d0e", due:"Apr 01, 2026",created:"Mar 03"},
        {title:"Audit Attendance Data",    assignee:"Mayank Mishra",  init:"MM", priority:"Low",     ps:"#eff6ff",pc:"#1d4ed8", status:"In Progress", ss:"#dbeafe",sc:"#1d4ed8", due:"Mar 20, 2026",created:"Mar 04"},
        {title:"Client Proposal Draft",    assignee:"Bhavesh Sattavan",init:"BS",priority:"High",    ps:"#fff7ed",pc:"#c2410c", status:"Completed",   ss:"#dcfce7",sc:"#166534", due:"Mar 05, 2026",created:"Feb 28"},
        {title:"Database Backup Setup",    assignee:"Omkar Chauhan",  init:"OC", priority:"Medium",  ps:"#f0fdf4",pc:"#15803d", status:"Pending",     ss:"#fef9c3",sc:"#854d0e", due:"Mar 25, 2026",created:"Mar 05"},
      ].map(({title,assignee,init,priority,ps,pc,status,ss,sc,due,created})=>(
        <div key={title} style={{display:"grid",gridTemplateColumns:"2.5fr 1.5fr 1fr 1fr 1.2fr 1.2fr 1.2fr",padding:"10px 14px",borderBottom:`1px solid ${C.border}`,alignItems:"center",fontSize:11}}>
          <div>
            <div style={{fontWeight:600,color:C.text,marginBottom:2}}>{title}</div>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:7}}>
            <div style={{width:26,height:26,borderRadius:"50%",background:"#6366f1",display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",fontSize:10,fontWeight:700,flexShrink:0}}>{init}</div>
            <span style={{color:C.sub,fontSize:11}}>{assignee}</span>
          </div>
          <span><span style={{padding:"2px 8px",borderRadius:20,background:ps,color:pc,fontSize:10,fontWeight:600}}>{priority}</span></span>
          <span><span style={{padding:"2px 8px",borderRadius:20,background:ss,color:sc,fontSize:10,fontWeight:600}}>{status}</span></span>
          <span style={{color:C.sub,fontSize:11}}>{due}</span>
          <span style={{color:C.muted,fontSize:11}}>{created}</span>
          <div style={{display:"flex",gap:5}}>
            {[["👁","#dbeafe","#1e40af"],["✏","#d1fae5","#065f46"],["🗑","#fee2e2","#991b1b"]].map(([ic,bg,cl])=>(
              <button key={ic} style={{width:26,height:26,borderRadius:7,border:"none",background:bg,color:cl,cursor:"pointer",fontSize:12,display:"flex",alignItems:"center",justifyContent:"center"}}>{ic}</button>
            ))}
          </div>
        </div>
      ))}
    </div>
  </div>
);

const SCREENS = {
  dashboard: Dashboard, users: Users, attendance: Attendance,
  tasks: Tasks, leaves: Leaves, documents: Documents, settings: Settings,
};

// ── Main Export ───────────────────────────────────────────────────────────────
export default function AdminPanelDemo() {
  const [active, setActive] = useState("dashboard");
  const [isMobile, setIsMobile] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const Screen = SCREENS[active] || Dashboard;
  const activeMenu = MENU.find(m => m.id === active);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 640);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  return (
    <div style={{display:"flex",flexDirection:"column",alignItems:"center",padding:"80px 16px",background:"#ffffff",fontFamily:"-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif"}}>

      {/* Section Header — matches WhyChooseUs exactly */}
      <div style={{textAlign:"center",marginBottom:80}}>
        <h2 style={{fontSize:"clamp(32px,5vw,48px)",fontWeight:700,color:"#111827",margin:"0 0 16px",lineHeight:1.15}}>
          Our Admin{" "}
<span style={{ background: "linear-gradient(to right, #16a34a, #059669)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>
                Panel
          </span>
        </h2>
        <p style={{fontSize:"clamp(15px,2vw,20px)",color:"#4b5563",margin:"0 auto",maxWidth:700,lineHeight:1.6}}>
          Powerful tools to manage your team
        </p>
      </div>

      {/* Browser Frame */}
      <div style={{width:"100%",maxWidth:1100,borderRadius:16,overflow:"hidden",boxShadow:"0 25px 60px rgba(0,0,0,0.2)",border:"1px solid #e2e8f0"}}>

        {/* Browser Bar */}
        <div style={{background:"#1e293b",padding:"10px 16px",display:"flex",alignItems:"center",gap:8}}>
          <div style={{display:"flex",gap:5}}>
            {["#ef4444","#f59e0b","#22c55e"].map(c=><div key={c} style={{width:10,height:10,borderRadius:"50%",background:c}}/>)}
          </div>
          <div style={{flex:1,background:"#334155",borderRadius:6,padding:"4px 12px",fontSize:11,color:"#94a3b8",textAlign:"center"}}>
            onenest.in/admin
          </div>
        </div>

        {/* App Shell */}
        <div style={{display:"flex",height:isMobile?520:580,background:C.bg,position:"relative"}}>

          {/* ── Sidebar ── */}
          {!isMobile && (
            <div style={{width:210,minWidth:210,background:"#fff",borderRight:"1.5px solid #e2e8f0",display:"flex",flexDirection:"column",flexShrink:0,overflow:"hidden"}}>

              {/* Logo */}
              <div style={{height:58,display:"flex",alignItems:"center",gap:10,padding:"0 16px",borderBottom:"1.5px solid #e2e8f0",flexShrink:0}}>
                <div style={{width:36,height:36,borderRadius:10,background:"linear-gradient(135deg,#4f46e5,#7c3aed)",display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",fontWeight:800,fontSize:16,flexShrink:0,boxShadow:"0 4px 12px rgba(79,70,229,0.4)"}}>A</div>
                <div>
                  <div style={{fontWeight:700,fontSize:13,color:"#0f172a",lineHeight:1.3}}>Admin Panel</div>
                  <div style={{fontSize:9,color:"#6366f1",fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase"}}>OneNest Connect</div>
                </div>
              </div>

              {/* Admin user pill */}
              <div style={{margin:"10px 10px 4px",padding:"8px 10px",background:"#f8fafc",border:"1.5px solid #e2e8f0",borderRadius:10,display:"flex",alignItems:"center",gap:8}}>
                <div style={{width:30,height:30,borderRadius:"50%",background:"linear-gradient(135deg,#6366f1,#8b5cf6)",display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",fontSize:11,fontWeight:700,flexShrink:0}}>AU</div>
                <div style={{flex:1,overflow:"hidden"}}>
                  <div style={{fontWeight:600,fontSize:11,color:"#0f172a",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>Admin User</div>
                  <div style={{fontSize:9,color:"#6b7280"}}>Super Admin</div>
                </div>
                <div style={{width:7,height:7,borderRadius:"50%",background:"#22c55e",boxShadow:"0 0 0 2px rgba(34,197,94,0.2)"}}/>
              </div>

              {/* Nav label */}
              <div style={{padding:"10px 16px 4px",fontSize:9,fontWeight:700,color:"#94a3b8",letterSpacing:"0.1em",textTransform:"uppercase"}}>Navigation</div>

              {/* Nav Items */}
              <nav style={{flex:1,overflowY:"auto",padding:"4px 8px"}}>
                {MENU.map(({id,icon,label})=>{
                  const isA = active===id;
                  return (
                    <div key={id} onClick={()=>setActive(id)} style={{display:"flex",alignItems:"center",gap:9,padding:"7px 9px",borderRadius:9,marginBottom:1,cursor:"pointer",position:"relative",transition:"all 0.15s",background:isA?"linear-gradient(135deg,#eef2ff,#f5f3ff)":"transparent",border:`1.5px solid ${isA?"#c7d2fe":"transparent"}`}}>
                      {isA && <div style={{position:"absolute",left:0,top:"20%",bottom:"20%",width:3,background:"linear-gradient(180deg,#4f46e5,#7c3aed)",borderRadius:"0 3px 3px 0"}}/>}
                      <div style={{width:28,height:28,borderRadius:7,display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,flexShrink:0,background:isA?"linear-gradient(135deg,#4f46e5,#7c3aed)":"#f1f5f9",boxShadow:isA?"0 3px 8px rgba(79,70,229,0.35)":"none"}}>
                        <span style={{filter:isA?"brightness(10)":"none"}}>{icon}</span>
                      </div>
                      <span style={{fontSize:12,fontWeight:isA?600:500,color:isA?"#3730a3":"#475569",whiteSpace:"nowrap"}}>{label}</span>
                    </div>
                  );
                })}
              </nav>

              {/* Bottom */}
              <div style={{padding:"8px 8px 10px",borderTop:"1.5px solid #e2e8f0"}}>
                <button style={{width:"100%",padding:"8px 10px",background:"#fff1f2",border:"1.5px solid #fecdd3",borderRadius:9,color:"#e11d48",fontSize:12,fontWeight:600,cursor:"pointer",display:"flex",alignItems:"center",gap:7,justifyContent:"center"}}>
                  <span style={{fontSize:14}}>↩</span> Logout
                </button>
              </div>
            </div>
          )}

          {/* Main Content */}
          <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden",minWidth:0}}>

            {/* Topbar */}
            <div style={{height:50,background:"#fff",borderBottom:`1px solid ${C.border}`,display:"flex",alignItems:"center",justifyContent:"space-between",padding:"0 16px",flexShrink:0}}>
              <div style={{display:"flex",alignItems:"center",gap:10}}>
                {isMobile && (
                  <div onClick={()=>setShowMenu(true)} style={{width:32,height:32,background:C.bg,border:`1px solid ${C.border}`,borderRadius:8,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",fontSize:16}}>☰</div>
                )}
                <div style={{height:6,width:180,background:C.bg,borderRadius:3}}/>
              </div>
              <div style={{display:"flex",alignItems:"center",gap:10}}>
                <div style={{position:"relative"}}>
                  <div style={{width:34,height:34,background:C.bg,border:`1px solid ${C.border}`,borderRadius:9,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",fontSize:16}}>🔔</div>
                  <div style={{position:"absolute",top:7,right:7,width:7,height:7,background:"#ef4444",borderRadius:"50%",border:"2px solid #fff"}}/>
                </div>
                <div style={{display:"flex",alignItems:"center",gap:7}}>
                  <div style={{width:32,height:32,borderRadius:"50%",background:"linear-gradient(135deg,#6366f1,#8b5cf6)",display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",fontSize:12,fontWeight:700}}>AU</div>
                  {!isMobile && <span style={{fontSize:12,fontWeight:600,color:C.text}}>Admin User</span>}
                </div>
              </div>
            </div>

            {/* Screen */}
            <div style={{flex:1,overflow:"hidden"}}>
              <Screen/>
            </div>
          </div>

          {/* Mobile Drawer */}
          {isMobile && showMenu && (
            <div style={{position:"absolute",inset:0,zIndex:50,display:"flex"}}>
              <div style={{width:220,background:"#fff",display:"flex",flexDirection:"column",height:"100%",overflow:"hidden",boxShadow:"4px 0 20px rgba(0,0,0,0.15)"}}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"12px 14px",borderBottom:"1.5px solid #e2e8f0"}}>
                  <div style={{display:"flex",alignItems:"center",gap:9}}>
                    <div style={{width:32,height:32,borderRadius:9,background:"linear-gradient(135deg,#4f46e5,#7c3aed)",display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",fontWeight:800,fontSize:14,boxShadow:"0 3px 8px rgba(79,70,229,0.4)"}}>A</div>
                    <div>
                      <div style={{fontWeight:700,fontSize:12,color:"#0f172a"}}>Admin Panel</div>
                      <div style={{fontSize:9,color:"#6366f1",fontWeight:600}}>OneNest Connect</div>
                    </div>
                  </div>
                  <div onClick={()=>setShowMenu(false)} style={{color:"#94a3b8",cursor:"pointer",fontSize:18,padding:"4px 8px",borderRadius:6,background:"#f1f5f9"}}>✕</div>
                </div>
                <div style={{flex:1,overflowY:"auto",padding:"8px"}}>
                  {MENU.map(({id,icon,label})=>{
                    const isA=active===id;
                    return (
                      <div key={id} onClick={()=>{setActive(id);setShowMenu(false);}} style={{display:"flex",alignItems:"center",gap:9,padding:"8px 10px",borderRadius:9,marginBottom:2,cursor:"pointer",background:isA?"linear-gradient(135deg,#eef2ff,#f5f3ff)":"transparent",border:`1.5px solid ${isA?"#c7d2fe":"transparent"}`}}>
                        <div style={{width:28,height:28,borderRadius:7,display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,background:isA?"linear-gradient(135deg,#4f46e5,#7c3aed)":"#f1f5f9",boxShadow:isA?"0 2px 8px rgba(79,70,229,0.35)":"none"}}>
                          <span style={{filter:isA?"brightness(10)":"none"}}>{icon}</span>
                        </div>
                        <span style={{fontSize:12,fontWeight:isA?600:500,color:isA?"#3730a3":"#475569"}}>{label}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div onClick={()=>setShowMenu(false)} style={{flex:1,background:"rgba(0,0,0,0.5)"}}/>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};